import { useEffect, useRef } from 'react';
import { useParams, Navigate } from 'react-router';
import { createRoot, Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from 'store';
import { useGetArticleBySlugQuery } from 'store/services/blog/blogApi';
import ArticleLayout from 'screens/Auth/Login/Articles/ArticleLayout';
import { SEOHelmet, BreadcrumbList, StructuredData } from 'components/SEO';
import { Decklist, DecklistProps } from 'components/Decklist';
import Footer from 'components/Footer';
import '../../../screens/Auth/Login/Articles/articles.css'; // Reuse existing article styles
import './DynamicArticle.css';

const DynamicArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isLoading, error } = useGetArticleBySlugQuery(slug || '');
  const contentRef = useRef<HTMLDivElement>(null);
  const decklistRootsRef = useRef<Map<HTMLElement, Root>>(new Map());

  // Set page title while loading
  useEffect(() => {
    if (article) {
      document.title = article.metaTitle || `${article.title} - TradeMagic`;
    }
  }, [article]);

  // Hydrate decklist components after content renders
  useEffect(() => {
    if (!contentRef.current || !article) return;

    // Copy ref value for cleanup
    const rootsMap = decklistRootsRef.current;

    // Find all decklist placeholder divs
    const decklistElements = contentRef.current.querySelectorAll('[data-type="decklist"]');

    decklistElements.forEach((element) => {
      const htmlElement = element as HTMLElement;

      // Skip if already hydrated
      if (decklistRootsRef.current.has(htmlElement)) return;

      try {
        // Parse decklist data from data attributes
        const title = htmlElement.dataset.title || 'Untitled Deck';
        const author = htmlElement.dataset.author || undefined;
        const format = htmlElement.dataset.format || undefined;
        const decklistId = htmlElement.dataset.id || undefined;

        // Parse mainboard
        let mainboard: DecklistProps['mainboard'] = [];
        if (htmlElement.dataset.mainboard) {
          try {
            mainboard = JSON.parse(htmlElement.dataset.mainboard);
          } catch (e) {
            console.error('Failed to parse mainboard data:', e);
          }
        }

        // Parse sideboard
        let sideboard: DecklistProps['sideboard'] = [];
        if (htmlElement.dataset.sideboard) {
          try {
            sideboard = JSON.parse(htmlElement.dataset.sideboard);
          } catch (e) {
            console.error('Failed to parse sideboard data:', e);
          }
        }

        // Create React root and render Decklist component
        const root = createRoot(htmlElement);
        decklistRootsRef.current.set(htmlElement, root);

        root.render(
          <Provider store={store}>
            <Decklist
              title={title}
              author={author}
              format={format}
              mainboard={mainboard}
              sideboard={sideboard}
              id={decklistId}
            />
          </Provider>
        );
      } catch (err) {
        console.error('Failed to hydrate decklist:', err);
      }
    });

    // Cleanup function to unmount React roots
    return () => {
      rootsMap.forEach((root) => {
        root.unmount();
      });
      rootsMap.clear();
    };
  }, [article]);

  // Loading state
  if (isLoading) {
    return (
      <div className="dynamic-article-loading">
        <div className="spinner-large"></div>
        <p>Loading article...</p>
      </div>
    );
  }

  // Error / Not found state
  if (error || !article) {
    return <Navigate to="/articles" replace />;
  }

  // Check if article is published
  if (article.status !== 'PUBLISHED') {
    return <Navigate to="/articles" replace />;
  }

  const formatPublishedDate = (isoDateString: string): string => {
    const date = new Date(isoDateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Prepare canonical URL (lowercase, no trailing slash)
  const canonicalUrl = `https://trademagic.gg/articles/${article.slug.toLowerCase()}`;

  return (
    <>
      {/* SEO Components */}
      <SEOHelmet
        title={article.metaTitle || `${article.title} - TradeMagic`}
        description={article.metaDescription || article.subtitle || ''}
        canonical={canonicalUrl}
        image={article.ogImageUrl || article.heroImageUrl || 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/banner_social_share.png'}
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg' },
          { name: 'Articles', url: 'https://trademagic.gg/articles' },
          { name: article.title, url: canonicalUrl },
        ]}
      />

      <StructuredData
        data={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: article.title,
          description: article.metaDescription || article.subtitle || '',
          image: article.heroImageUrl || article.ogImageUrl,
          datePublished: article.publishedAt,
          dateModified: article.updatedAt,
          author: {
            '@type': 'Organization',
            name: 'TradeMagic',
          },
          publisher: {
            '@type': 'Organization',
            name: 'TradeMagic',
            logo: {
              '@type': 'ImageObject',
              url: 'https://trademagic.gg/logo.png',
            },
          },
        }}
      />

      {/* Article Layout */}
      <ArticleLayout
        title={article.title}
        hero={article.heroImageUrl || ''}
      >
        <article className="article-body">
          {/* Subtitle */}
          {article.subtitle && (
            <p className="article-subtitle-text">{article.subtitle}</p>
          )}

          {/* Published date */}
          {article.publishedAt && (
            <p className="article-published-date">
              Published on {formatPublishedDate(article.publishedAt)}
            </p>
          )}

          {/* Article content (HTML from TipTap) */}
          <div
            ref={contentRef}
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />
        </article>

        <Footer />
      </ArticleLayout>
    </>
  );
};

export default DynamicArticle;
